import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Smartphone, Lock, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import Navbar from './Navbar';
import Footer from './Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppRedirectPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const type = searchParams.get('type') || 'transporter';
  const path = window.location.pathname;
  const isResetPassword = path.includes('reset-password');
  const isMagicLink = path.includes('magic-link');

  if (isResetPassword) {
    return <ResetPasswordView token={token} type={type} />;
  }
  if (isMagicLink) {
    return <MagicLinkView token={token} />;
  }
  return null;
};

/* ───── Password Reset ───── */
const ResetPasswordView = ({ token, type }) => {
  const [showWebForm, setShowWebForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({ newPassword: '', confirmPassword: '' });

  useEffect(() => {
    // Try to open app via deep link for password reset
    const deepLink = `horsego://auth/reset-password?token=${token}&type=${type}`;
    window.location.href = deepLink;
    // Show web fallback after 2s
    const timer = setTimeout(() => setShowWebForm(true), 2000);
    return () => clearTimeout(timer);
  }, [token, type]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.newPassword || !formData.confirmPassword) { setError('Please fill in all fields'); return; }
    if (formData.newPassword.length < 6) { setError('Password must be at least 6 characters long'); return; }
    if (formData.newPassword !== formData.confirmPassword) { setError('Passwords do not match'); return; }

    setIsLoading(true);
    setError('');
    try {
      const endpoint = type === 'customer'
        ? `${API}/customers/password-reset/confirm`
        : `${API}/transporters/password-reset/confirm`;
      await axios.post(endpoint, { token, new_password: formData.newPassword });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. The link may have expired.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageWrapper>
      {/* Loading state while waiting for app to open */}
      {!showWebForm && !success && (
        <div className="text-center" data-testid="app-redirect-loading">
          <PhoneIcon />
          <h1 className="text-2xl font-bold text-white mb-4">Opening HorseGO App...</h1>
          <div className="flex items-center justify-center gap-2 text-gray-400 mb-6">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Redirecting to app...</span>
          </div>
        </div>
      )}

      {/* Inline password reset form */}
      {showWebForm && !success && (
        <>
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-amber-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-amber-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Reset Password</h1>
            <p className="text-gray-400 text-sm">Enter your new password below</p>
          </div>

          {error && <ErrorBanner message={error} />}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="app-reset-password-form">
            <PasswordField label="New Password" name="newPassword" value={formData.newPassword} show={showPassword} toggle={() => setShowPassword(!showPassword)} onChange={(v) => { setFormData(p => ({ ...p, newPassword: v })); setError(''); }} testId="app-new-password-input" />
            <PasswordField label="Confirm Password" name="confirmPassword" value={formData.confirmPassword} show={showConfirmPassword} toggle={() => setShowConfirmPassword(!showConfirmPassword)} onChange={(v) => { setFormData(p => ({ ...p, confirmPassword: v })); setError(''); }} testId="app-confirm-password-input" />
            <Button type="submit" disabled={isLoading} className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-6 rounded-full text-lg" data-testid="app-reset-submit-btn">
              {isLoading ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" />Resetting...</>) : (<><Lock className="w-5 h-5 mr-2" />Reset Password</>)}
            </Button>
          </form>
        </>
      )}

      {/* Success */}
      {success && (
        <SuccessView title="Password Reset Successful!" message="Your password has been changed. You can now log in with your new password in the HorseGO app." />
      )}

      <AppStoreLink />
    </PageWrapper>
  );
};

/* ───── Magic Link ───── */
const MagicLinkView = ({ token }) => {
  const [showFallback, setShowFallback] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) {
      setErrorMsg('Invalid or missing magic link token.');
      setShowFallback(true);
      return;
    }

    // Step 1: Immediately try to open the app with the token
    // The app will verify the token itself and log the user in
    window.location.href = `horsego://auth/magic-link?token=${token}`;

    // Step 2: After 3s, if user is still here, show web fallback
    const timer = setTimeout(() => setShowFallback(true), 3000);
    return () => clearTimeout(timer);
  }, [token]);

  const handleWebVerify = async () => {
    setVerifying(true);
    setErrorMsg('');
    try {
      const res = await axios.post(`${API}/auth/magic-link/verify`, { token });
      if (res.data && res.data.session_token) {
        setVerified(true);
      } else {
        setErrorMsg('Magic link expired or invalid. Please request a new one.');
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Magic link expired or invalid. Please request a new one.');
    } finally {
      setVerifying(false);
    }
  };

  const handleRetryApp = () => {
    window.location.href = `horsego://auth/magic-link?token=${token}`;
  };

  return (
    <PageWrapper>
      {/* Loading: trying to open the app */}
      {!showFallback && !verified && (
        <div className="text-center" data-testid="magic-link-loading">
          <PhoneIcon />
          <h1 className="text-2xl font-bold text-white mb-4">Opening HorseGO App...</h1>
          <div className="flex items-center justify-center gap-2 text-gray-400 mb-6">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Redirecting to app...</span>
          </div>
        </div>
      )}

      {/* Fallback: app didn't open */}
      {showFallback && !verified && (
        <div className="text-center" data-testid="magic-link-fallback">
          <PhoneIcon />
          <h1 className="text-2xl font-bold text-white mb-4">Login to HorseGO</h1>

          {errorMsg && <ErrorBanner message={errorMsg} />}

          {!errorMsg && (
            <>
              <p className="text-gray-400 mb-6">If the app didn't open, tap below to try again or verify here.</p>

              <button onClick={handleRetryApp} className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-4 px-8 rounded-full text-lg transition-colors mb-3" data-testid="retry-open-app-btn">
                Open HorseGO App
              </button>

              <button onClick={handleWebVerify} disabled={verifying} className="w-full bg-white/10 hover:bg-white/20 text-white font-semibold py-3 px-8 rounded-full transition-colors text-sm" data-testid="verify-magic-link-btn">
                {verifying ? 'Verifying...' : "Can't open the app? Verify here"}
              </button>
            </>
          )}

          {errorMsg && (
            <button onClick={() => { window.location.href = 'horsego://'; }} className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-4 px-8 rounded-full text-lg transition-colors" data-testid="open-app-btn">
              Open HorseGO App
            </button>
          )}
        </div>
      )}

      {/* Verified on web */}
      {verified && (
        <div className="text-center" data-testid="magic-link-success">
          <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-xl font-bold text-white mb-3">Login Verified!</h2>
          <p className="text-gray-400 mb-6">Your account is ready. Open the app and log in with your email.</p>
          <button onClick={() => { window.location.href = 'horsego://'; }} className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-4 px-8 rounded-full text-lg transition-colors" data-testid="open-app-after-verify-btn">
            Open HorseGO App
          </button>
        </div>
      )}

      <AppStoreLink />
    </PageWrapper>
  );
};

/* ───── Shared components ───── */

const PageWrapper = ({ children }) => (
  <div className="min-h-screen bg-[#0a0a0a]">
    <Navbar />
    <section className="relative pt-32 pb-24 overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-900/20 via-transparent to-teal-900/20" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
      </div>
      <div className="max-w-md mx-auto px-4 relative z-10">
        <div className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-3xl p-8 md:p-10">
          {children}
        </div>
      </div>
    </section>
    <Footer />
  </div>
);

const PhoneIcon = () => (
  <div className="w-20 h-20 bg-amber-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
    <Smartphone className="w-10 h-10 text-amber-500" />
  </div>
);

const ErrorBanner = ({ message }) => (
  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6">
    <p className="text-red-500 text-sm text-center">{message}</p>
  </div>
);

const SuccessView = ({ title, message }) => (
  <div className="text-center" data-testid="app-reset-success">
    <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
      <CheckCircle className="w-10 h-10 text-green-500" />
    </div>
    <h2 className="text-xl font-bold text-white mb-3">{title}</h2>
    <p className="text-gray-400 mb-6">{message}</p>
    <button onClick={() => { window.location.href = 'horsego://'; }} className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-4 px-8 rounded-full text-lg transition-colors" data-testid="open-app-btn">
      Open HorseGO App
    </button>
  </div>
);

const PasswordField = ({ label, name, value, show, toggle, onChange, testId }) => (
  <div>
    <Label className="text-gray-300 mb-2 block">{label}</Label>
    <div className="relative">
      <Input name={name} type={show ? 'text' : 'password'} value={value} onChange={(e) => onChange(e.target.value)} placeholder={label} className="bg-black/30 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl pr-12" data-testid={testId} />
      <button type="button" onClick={toggle} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
        {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
      </button>
    </div>
  </div>
);

const AppStoreLink = () => null;

export default AppRedirectPage;
